'use client'

import { ROUTES } from '@/constants/routes'
import { motion } from 'framer-motion'
import { Facebook, Instagram, Linkedin, Twitter, Mail, MapPin, Phone } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { LandingPageData } from '@/redux/api/landingPageApi'
import { navLinks } from '@/data/landing'
import useSettings from '@/hooks/useSettings'
import { getMediaUrl } from '@/utils'

import { useGetPublicPagesQuery } from '@/redux/api/pageApi'

export default function LandingFooter({ data }: { data?: LandingPageData['footer'] }) {
  const { t } = useTranslation()
  const { settings } = useSettings()
  const { data: pagesData } = useGetPublicPagesQuery()
  const pages = pagesData?.pages || []

  const landingLogoUrl = settings?.landing_logo_url ? getMediaUrl(settings.landing_logo_url) : '/images/light-logo1.png'

  return (
    <footer className="relative border-t border-white/5 bg-primary/2 pt-12 md:pt-20 pb-8 md:pb-10">
      {/* Dot Grid Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(./images/bg.png)`,
          width: '100%',
          height: '100%',
        }}
      />

      {/* Background Decorative Glows */}
      <div className="absolute bottom-[-100px] left-1/2 -translate-x-1/2 w-[60%] h-[400px] bg-primary/15 rounded-full blur-[150PX] pointer-events-none" />
      <div className="absolute bottom-[-60px] left-1/2 -translate-x-1/2 w-[40%] h-[200px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="container mx-auto px-6 relative z-10"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-8">
          <div className="space-y-6">
            <Link href="/" className="inline-block">
              <Image
                src={landingLogoUrl as string}
                alt={settings?.app_name || 'Logo'}
                width={150}
                height={40}
                className="h-8 w-auto object-contain"
                unoptimized
              />
            </Link>
            <p className="text-white/40 text-sm leading-relaxed max-w-xs">
              {data?.tagline || t('footer_tagline')}
            </p>
            <div className="flex items-center gap-4">
              {data?.social_links?.map((social, i) => {
                const Icon =
                  {
                    Facebook,
                    Twitter,
                    Instagram,
                    Linkedin,
                  }[social.icon] || Mail

                return (
                  <motion.a
                    key={i}
                    href={social.href}
                    whileHover={{ y: -3, scale: 1.1 }}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-primary hover:border-primary/50 transition-all"
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                )
              })}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">{t('platform')}</h4>
            <ul className="space-y-4">
              {navLinks.map((item) => (
                <li key={item?.label}>
                  <Link href={item.href} className="text-white/40 hover:text-white transition-colors text-sm">
                    {t(item?.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {pages.length > 0 && (
            <div>
              <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">{t('company')}</h4>
              <ul className="space-y-4">
                {pages.map((page) => (
                  <li key={page.id || page._id}>
                    <Link href={`/p/${page.slug}`} className="text-white/40 hover:text-white transition-colors text-sm">
                      {page.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">{t('contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-white/40 text-sm">
                <MapPin className="w-5 h-5 text-primary shrink-0" />
                <span>{data?.address || '123 AI Street, Tech City, TC 12345'}</span>
              </li>
              <li className="flex items-center gap-3 text-white/40 text-sm">
                <Phone className="w-5 h-5 text-primary shrink-0" />
                <span>{data?.phone || '+1 (234) 567-890'}</span>
              </li>
              <li className="flex items-center gap-3 text-white/40 text-sm">
                <Mail className="w-5 h-5 text-primary shrink-0" />
                <span>{data?.email || 'support@example.ai'}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-white/20 text-[10px] sm:text-xs font-bold uppercase tracking-widest text-center md:text-left">
          <p className="max-w-sm md:max-w-none leading-relaxed">
            {data?.copyright || `© ${new Date().getFullYear()} Smart AI Content Generation Suite. ALL RIGHTS RESERVED.`}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
            <Link href="#" className="hover:text-white transition-colors">
              TERMS OF SERVICE
            </Link>
            <Link href="#" className="hover:text-white transition-colors">
              COOKIE POLICY
            </Link>
          </div>
        </div>
      </motion.div>
    </footer>
  )
}
